import type React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';
import * as tradeActionTypes from '@makemydeal/dr-dash-store/actionTypes/tradeActionTypes';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';

import { GENERIC_LAST_FIELD_CHANGED_ERROR, usePaymentCall, dealLockHooks } from '@makemydeal/dr-dash-components';
import { formatNumberAndCents, kebabCase } from '@makemydeal/dr-common-utils';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

export const usePayoffField = () => {
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const hasSingleIncludedTradeIn = useSelector(tradeInsSelectors.hasSingleIncludedTradeIn);
    const isGCDynamicValidationsEnabled = useSelector(featureToggleSelectors.isGCDynamicValidationsEnabled);
    const firstIncludedTradeInIndex = useSelector(tradeInsSelectors.getFirstIncludedTradeInIndex);
    const tradeIns = useSelector(tradeInsSelectors.getActiveTradeIns);

    const isLocked = dealLockHooks.useIsDealLocked();
    const {
        value: payoff,
        handleChange: changePayoff,
        handleBlur: payoffBlur,
        handleKeyDown
    } = usePaymentCall(
        tradeInsSelectors.getIncludedTradeInsTotalAmountOwed,
        tradeInsActionCreators.payoffChanged(firstIncludedTradeInIndex),
        {
            formatValueToString: formatNumberAndCents,
            ignoreTextValue: () => false
        }
    );

    const failedPayoff = useSelector(deskingSelectors.wasTheLastFailure(tradeActionTypes.PAYOFF_CHANGED));
    const dispatch = useDispatch();
    const fieldLabel = tradeIns.length > 1 ? 'Total Trade Payoff' : 'Trade Payoff';
    const fieldName = tradeIns.length > 1 ? kebabCase(fieldLabel) : kebabCase('Payoff');

    const handleClick = () => {
        dispatch(baseDealActionCreators.openSlideOut(baseDealActionCreators.SlideOutType.TradeIn));
        dispatch(interactionAnalyticsActionCreators.mvTradePayoffLink());
    };

    const shouldSyncRedDotErrors = isDealScreenRedDotErrorsEnabled || isGCDynamicValidationsEnabled;

    useEffect(() => {
        if (shouldSyncRedDotErrors) {
            dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.SUMMARY, fieldName, failedPayoff));
        }
    }, [shouldSyncRedDotErrors, dispatch, failedPayoff]);

    const handleLabelKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
        }
    };

    return {
        label: fieldLabel,
        errorBadgeEntity: DealTabs.TRADE_IN,
        onLabelActivate: handleClick,
        onLabelKeyDown: handleLabelKeyDown,
        inputProps: {
            inputPrefix: '$',
            autoInsertCommas: true,
            allowDecimal: true,
            decimalMaxLength: 2,
            decimalMinLength: 0,
            'data-testid': 'payoff',
            name: fieldName,
            onChange: changePayoff,
            placeholder: '10,000.00',
            value: payoff,
            hasError: failedPayoff,
            errorMessage: GENERIC_LAST_FIELD_CHANGED_ERROR,
            onBlur: payoffBlur,
            disabled: isLocked || !hasSingleIncludedTradeIn,
            size: 'sm' as const,
            onKeyDown: handleKeyDown
        }
    };
};
